import {
  DEAD_AFTER_MS,
  HEARTBEAT_MS,
  PROTOCOL_VERSION,
  type ClientMessage,
  type ServerMessage,
} from "@squire/core";
import type { DeviceSettings } from "../app/device.js";
import type { ConnectionStatus, Transport } from "./transport.js";

const MAX_QUEUE = 20;
const PING = JSON.stringify({ t: "ping" });

/** Exponential backoff with jitter: 1 s, 2 s, 4 s ... max 30 s. */
export function backoffDelay(attempt: number, random = Math.random): number {
  const base = Math.min(30_000, 1000 * 2 ** Math.min(attempt, 5));
  return Math.round(base * (0.75 + random() * 0.5));
}

/** The token rides in the subprotocol (hex, header-safe), never in the URL that servers and proxies log. */
export function wsProtocols(token: string): string[] {
  const hex = Array.from(new TextEncoder().encode(token), (b) => b.toString(16).padStart(2, "0")).join("");
  return ["squire", `auth.${hex}`];
}

export function wsUrl(device: DeviceSettings): string {
  const base = device.serverUrl.replace(/\/$/, "").replace(/^http/, "ws");
  const q = new URLSearchParams({
    device: device.id,
    name: device.name,
    profile: device.profile,
  });
  return `${base}/api/ws?${q.toString()}`;
}

/**
 * WebSocket transport with heartbeat, dead-connection detection and reconnect with backoff.
 * After every (re)connect it says hello and the server answers with a full state snapshot.
 */
export class WsTransport implements Transport {
  onMessage: (msg: ServerMessage) => void = () => {};
  onStatus: (status: ConnectionStatus) => void = () => {};
  private ws: WebSocket | null = null;
  private attempt = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private lastSeen = 0;
  private running = false;
  private queue: string[] = [];
  /** Another tab of the same device took over the connection; wait until this tab is visible again. */
  private replaced = false;

  constructor(
    private readonly device: DeviceSettings,
    private readonly visible: () => boolean,
    private readonly tz: string,
  ) {}

  start(): void {
    if (this.running) return;
    this.running = true;
    window.addEventListener("online", this.onOnline);
    document.addEventListener("visibilitychange", this.onVisible);
    this.connect();
  }

  stop(): void {
    this.running = false;
    window.removeEventListener("online", this.onOnline);
    document.removeEventListener("visibilitychange", this.onVisible);
    this.clearTimers();
    this.ws?.close(1000, "bye");
    this.ws = null;
  }

  send(msg: ClientMessage): boolean {
    const data = JSON.stringify(msg);
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(data);
      return true;
    }
    if (msg.t === "ping") return false;
    this.queue.push(data);
    if (this.queue.length > MAX_QUEUE) this.queue.shift();
    return false;
  }

  private onVisible = () => {
    if (this.replaced && !document.hidden) {
      this.replaced = false;
      this.attempt = 0;
      this.reconnectSoon(100);
    }
  };

  private onOnline = () => {
    if (this.ws?.readyState !== WebSocket.OPEN) {
      this.attempt = 0;
      this.reconnectSoon(100);
    }
  };

  private clearTimers() {
    if (this.timer) clearTimeout(this.timer);
    if (this.heartbeat) clearInterval(this.heartbeat);
    this.timer = null;
    this.heartbeat = null;
  }

  private connect() {
    this.clearTimers();
    if (!this.running) return;
    this.onStatus("connecting");
    let ws: WebSocket;
    try {
      ws = new WebSocket(wsUrl(this.device), wsProtocols(this.device.token));
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;
    ws.onopen = () => {
      this.attempt = 0;
      this.lastSeen = Date.now();
      this.onStatus("online");
      ws.send(
        JSON.stringify({
          t: "hello",
          v: PROTOCOL_VERSION,
          device: { id: this.device.id, name: this.device.name, profile: this.device.profile },
          tz: this.tz,
          visible: this.visible(),
        } satisfies ClientMessage),
      );
      for (const m of this.queue.splice(0)) ws.send(m);
      this.heartbeat = setInterval(() => {
        if (Date.now() - this.lastSeen > DEAD_AFTER_MS) {
          // No pong for too long: the socket is dead even if the browser hasn't noticed.
          ws.close(4001, "dead");
          return;
        }
        ws.send(PING);
      }, HEARTBEAT_MS);
    };
    ws.onmessage = (e) => {
      this.lastSeen = Date.now();
      let msg: ServerMessage;
      try {
        msg = JSON.parse(String(e.data)) as ServerMessage;
      } catch {
        return;
      }
      if (msg.t !== "pong") this.onMessage(msg);
    };
    ws.onclose = (e) => {
      if (this.ws !== ws) return;
      this.ws = null;
      this.clearTimers();
      this.onStatus("offline");
      if (e.code === 4003 || e.code === 1008) return; // auth problems: wait for the user
      if (e.code === 4000) {
        // Same device opened in another tab: don't fight over the socket.
        this.replaced = true;
        return;
      }
      this.scheduleReconnect();
    };
    ws.onerror = () => {
      /* onclose follows */
    };
  }

  private scheduleReconnect() {
    if (!this.running) return;
    this.reconnectSoon(backoffDelay(this.attempt++));
  }

  private reconnectSoon(ms: number) {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => this.connect(), ms);
  }
}
